import React, { useRef, useEffect, useState } from 'react'
import { View, FlatList, Dimensions } from 'react-native'
import PropTypes from 'prop-types'

import StoreCard from '../StoreCard/StoreCard'

import style from './StoresCardsList.Styles'

const SCREEN_WIDTH = Dimensions.get('screen').width
const CARD_SIZE = SCREEN_WIDTH * 0.92
const EMPTY_CARD_SIZE = (SCREEN_WIDTH - CARD_SIZE) / 2

const StoreCardItem = ({
  store,
  index,
  stores,
  isLoading,
  onShowTimeSlotsPress,
  showTimeSlotButton,
  addressTestID,
  getStoresOpenHours,
  selectedIndex
}) => {
  const [email, setEmail] = useState('')
  const [openTimesData, setOpenTimesData] = useState({
    loading: true,
    calendar: null
  })

  const isActive = selectedIndex === index

  useEffect(() => {
    if (isActive && store?.id && !openTimesData.calendar) {
      setOpenTimesData(prev => ({ ...prev, loading: true }))
      getStoresOpenHours(store?.id, {
        onSuccess: res => {
          setEmail(res?.email)
          setOpenTimesData(prev => ({
            ...prev,
            loading: false,
            calendar: res?.calendar
          }))
        },
        onError: () => setOpenTimesData(prev => ({ ...prev, loading: false }))
      })
    }
  }, [isActive, store])

  const EmptyCard = <View style={{ width: EMPTY_CARD_SIZE }} />

  return (
    <View style={style.cardContainer}>
      {index === 0 ? EmptyCard : null}
      <StoreCard
        isLoading={isLoading}
        store={store}
        cardSize={CARD_SIZE}
        onShowTimeSlotsPress={() =>
          onShowTimeSlotsPress({ ...store, email: email })
        }
        showTimeSlotButton={showTimeSlotButton}
        addressTestID={addressTestID}
        style={{ paddingHorizontal: 5 }}
        calendar={openTimesData?.calendar}
        calendarLoading={openTimesData?.loading}
      />
      {index === stores.length - 1 ? EmptyCard : null}
    </View>
  )
}

const StoresCardsList = ({
  stores,
  isLoading,
  selectedStoreIndex = 0,
  onStoreChange,
  onShowTimeSlotsPress,
  showTimeSlotButton,
  getStoresOpenHours
}) => {
  const flatListRef = useRef()
  const [_selectedStoreIndex, setSelectedStoreIndex] =
    useState(selectedStoreIndex)
  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 90 })

  const onViewableItemsChanged = useRef(({ viewableItems }) => {
    if (!viewableItems[0]) {
      return
    }

    setSelectedStoreIndex(viewableItems[0].index)
    onStoreChange(viewableItems[0].index)
  })

  const handleSnapToStoreCard = () => {
    flatListRef.current.scrollToIndex({
      animated: false,
      index: selectedStoreIndex
    })
  }

  useEffect(() => {
    if (selectedStoreIndex !== _selectedStoreIndex) {
      handleSnapToStoreCard()
    }
  }, [selectedStoreIndex])

  let contentContainerStyle =
    Dimensions.get('window').width > 700
      ? { justifyContent: 'center', flex: 1 }
      : {}

  return (
    <FlatList
      scrollEnabled={!isLoading}
      contentContainerStyle={contentContainerStyle}
      ref={flatListRef}
      extraData={isLoading}
      keyExtractor={item => item.id}
      showsHorizontalScrollIndicator={false}
      horizontal
      data={isLoading ? [{ id: 0 }, { id: 1 }] : stores}
      snapToInterval={CARD_SIZE}
      decelerationRate={0}
      getItemLayout={(data, index) => {
        if (index === 0 || index === stores.length - 1) {
          return {
            length: CARD_SIZE,
            offset: (CARD_SIZE + EMPTY_CARD_SIZE) * index,
            index
          }
        }
        return { length: CARD_SIZE, offset: CARD_SIZE * index, index }
      }}
      onViewableItemsChanged={onViewableItemsChanged.current}
      viewabilityConfig={viewabilityConfig.current}
      renderItem={({ item, index }) => {
        return (
          <StoreCardItem
            store={item}
            index={index}
            stores={stores}
            isLoading={isLoading}
            onShowTimeSlotsPress={onShowTimeSlotsPress}
            showTimeSlotButton={showTimeSlotButton}
            getStoresOpenHours={getStoresOpenHours}
            selectedIndex={_selectedStoreIndex}
          />
        )
      }}
      initialScrollIndex={_selectedStoreIndex}
    />
  )
}

StoresCardsList.propTypes = {
  stores: PropTypes.array,
  isLoading: PropTypes.bool,
  selectedStoreIndex: PropTypes.number,
  onStoreChange: PropTypes.func,
  onShowTimeSlotsPress: PropTypes.func,
  showTimeSlotButton: PropTypes.string,
  getStoresOpenHours: PropTypes.func
}

export default StoresCardsList
